import * as THREE from 'three';
import { backgroundColor, textColor, font } from './styles360.js';
import { DefaultSizing, InteractionValues } from './enums.js';

/**
* Provides a mesh that can be rendered with a camera or scene based upon the message and wanted height.
* @param {string} message
* @param {number} padding //provides distance between when the text ends and the background ends, similar to padding in CSS
* @param {number} height
*/
export function createMessage(message, padding = .25, height = DefaultSizing.Height, width = null, opacity = 1, messageColor = textColor()) {
	const textMesh = createMessageTextMesh(message, height, messageColor);

	const textMeshMetrics = textMesh.geometry.parameters;

	//create the background mesh behind the text that scales to the length of the mesh of the text
	let renderWidth;

	if (!width) {
		renderWidth = textMeshMetrics.width + padding;
	} else {
		renderWidth = width;
	}

	const renderHeight = textMeshMetrics.height + padding;

	const backgroundMesh = createBackgroundMesh(renderWidth, renderHeight, backgroundColor(), opacity);
	backgroundMesh.add(textMesh);
	textMesh.position.set(0, 0, DefaultSizing.TextOffset);
	return backgroundMesh;
}

/**
* Creates a pill-shaped button based upon the passed in parameters.
* @param {any} message
* @param {any} padding
* @param {any} height
* @param {any} buttonColor
* @param {any} btnTextColor
*/
export function createButton(message, padding = .25, height = DefaultSizing.Height, buttonColor = backgroundColor(), btnTextColor = textColor()) {
	const textMesh = createMessageTextMesh(message, height, btnTextColor);
	const textMeshMetrics = textMesh.geometry.parameters;

	const renderHeight = textMeshMetrics.height + padding;
	const renderWidth = textMeshMetrics.width + padding;

	const backgroundMesh = createBackgroundMesh(renderWidth, renderHeight, buttonColor);
	const leftSideBtn = createHalfCircle(renderHeight, buttonColor);
	const rightSideBtn = createHalfCircle(renderHeight, buttonColor);

	//creates an invisible mesh behind the button pieces so that the raycaster can interact with all of it
	const btnGroupMesh = createBackgroundMesh(renderWidth + renderHeight, renderHeight + padding, buttonColor, 0);
	btnGroupMesh.add(backgroundMesh);
	btnGroupMesh.add(textMesh);
	btnGroupMesh.add(leftSideBtn);
	btnGroupMesh.add(rightSideBtn);

	textMesh.position.set(0, 0, DefaultSizing.TextOffset);
	leftSideBtn.position.set(-(renderWidth / 2), 0, 0);
	rightSideBtn.position.set(renderWidth / 2, 0, 0);
	leftSideBtn.rotateOnAxis(new THREE.Vector3(0, 0, 1), 90 / 180 * Math.PI); //inline math here to highlight radians, not degrees for rotation
	rightSideBtn.rotateOnAxis(new THREE.Vector3(0, 0, 1), -90 / 180 * Math.PI);

	return btnGroupMesh;
}

/**
* Converts the text given into a text mesh with the text rendered onto it.
* @param {any} message
* @param {any} height
* @param {any} fontFamily
*/
export function createMessageTextMesh(message, height = DefaultSizing.Height, messageColor = textColor(), fontFamily = font(), outlineColor = null) {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');

	//depending on the message, we need to scale the meshes to fully fit the message
	const contextFont = `normal ${DefaultSizing.Scale}px ${fontFamily}`;
	context.font = contextFont;
	const metrics = context.measureText(message);

	canvas.width = metrics.width;
	canvas.height = DefaultSizing.Scale;

	//figure out the color and then render the message on the context
	renderMessage(context, message, metrics.width, messageColor, contextFont, outlineColor);

	//create the mesh with the canvas that has just been rendered to
	return createTextMesh(canvas, metrics.width, height, messageColor);
}

/**
 * Returns a mesh based upon the dimensions and color provided.
 * @param {number} x
 * @param {number} y
 * @param {string} color
 */
export function createBackgroundMesh(x, y, color, opacity = 1) {
	const geometry = new THREE.PlaneGeometry(x, y);
	let material;
	//let's only add the 'transparent: true' property if the opacity is actually not 1
	if (opacity === 1) {
		material = new THREE.MeshBasicMaterial({
			color: color,
			side: THREE.DoubleSide
		});
	} else {
		material = new THREE.MeshBasicMaterial({
			color: color,
			side: THREE.DoubleSide,
			transparent: true,
			opacity: opacity,
			depthWrite: (opacity !== 0),
			depthTest: (opacity !== 0)
		});
	}
	const mesh = new THREE.Mesh(geometry, material);
	return mesh;
}

/**
 * Creates a circle texture that slowly fills in when progress is made.
 * @param {number} size
 */
export function createLoadingIndicator(size = .5) {
	const material = updateLoadingIndicator(0);
	const geometry = new THREE.PlaneGeometry(size, size);
	const mesh = new THREE.Mesh(geometry, material);
	mesh.renderOrder = 10;
	return mesh;
}

/**
 * Fills in the loading indicator with the percentage between how much time has passed and the amount of time that needs to pass for something to happen.
 * @param {number} viewTime
 * @param {number} triggerDelay
 */
export function updateLoadingIndicator(viewTime, triggerDelay, color = '#FFFFFF') {
	if (!triggerDelay) {
		triggerDelay = InteractionValues.TriggerDelay;
	}
	const percentLoaded = viewTime / triggerDelay;

	return new THREE.MeshBasicMaterial({
		color: color,
		side: THREE.DoubleSide,
		map: createCircleTexture(color, 50, percentLoaded),
		transparent: true,
		opacity: .75,
	});
}

/**
 * Generates a texture of an outlined colored circle that it as completed as the percent filled parameter
 * @param {any} color
 * @param {any} strokeWidth
 * @param {any} percentFilled
 */
export function createCircleTexture(color, strokeWidth, percentFilled) {
	//canvas textures are rendered as 256 x 256, any larger and it'll throw a warning and auto-scale it down
	const textureMaxSize = 256;
	//when drawing the circle, space is needed between the end of the circle and the end of the box that the texture is in
	//otherwise, the it'll look like a little squished as it runs out of room
	const bufferSpace = 5;

	const matCanvas = document.createElement('canvas');
	matCanvas.width = matCanvas.height = textureMaxSize;

	const matContext = matCanvas.getContext('2d');
	matContext.lineWidth = strokeWidth;
	matContext.strokeStyle = color;

	const texture = new THREE.Texture(matCanvas);
	const circleSize = textureMaxSize - strokeWidth - bufferSpace;
	const center = textureMaxSize / 2;
	matContext.beginPath();
	//5th parameter here deals with how far the arc is drawn, and since 2π radians = 360 degree, we use the percent to control how much of the circle we draw
	matContext.arc(center, center, circleSize / 2, 0, percentFilled * 2 * Math.PI, false);

	if (percentFilled >= 1) {
		matContext.closePath();
	}

	matContext.stroke();
	texture.needsUpdate = true;
	return texture;
}

/**
* Deletes the object by disposing of its parts
* @param {any} object
*/
export function deleteObject(object) {
	if (!(object instanceof THREE.Object3D)) {
		return false;
	}

	// for better memory management and performance, dispose of the individual parts
	if (object.geometry) {
		object.geometry.dispose();
	}

	if (object.material) {
		if (object.material instanceof Array) {
			object.material.forEach(material => material.dispose());
		} else {
			object.material.dispose();
		}
	}
	
	return true;
}

/**
 * Creates a mesh based upon the canvas (which should have text written on its context)
 * and sizes it based upon the provided dimensions.
 * @param {HTMLCanvasElement} canvas
 * @param {number} width
 * @param {number} height
 * @param {string} textColor
 */
function createTextMesh(canvas, width, height, textColor) {
	const texture = new THREE.Texture(canvas);
	texture.needsUpdate = true;

	const material = new THREE.MeshBasicMaterial({
		color: textColor,
		side: THREE.DoubleSide,
		map: texture,
		transparent: true,
		depthWrite: false,
		depthTest: false
	});

	const geometry = new THREE.PlaneGeometry(
		(height * width) / DefaultSizing.Scale,
		height
	);

	const plane = new THREE.Mesh(geometry, material);
	return plane;
}

/**
 * Creates a half circle based upon size and color. Most useful for creating rounded edges
 * @param {any} size
 * @param {any} color
 */
function createHalfCircle(size, color) {
	return new THREE.Mesh(
		new THREE.CircleGeometry(size / 2, 90, 0, Math.PI), //CircleGeometry uses radius, not diameter, so we need to divide by 2 to get it to be the same height as a plane geometry
		new THREE.MeshBasicMaterial({
			color: color
		})
	);
}

/**
 * Updates the provided context with the provided formatting and renders the message onto the context.
 * @param {CanvasRenderingContext2D} context
 * @param {string} message
 * @param {number} width
 * @param {string} textColor
 * @param {string} font
 */
function renderMessage(context, message, width, textColor, font, outlineColor) {
	context.font = font;
	context.textAlign = 'center';
	context.textBaseline = 'middle';

	if (outlineColor) {
		context.strokeStyle = outlineColor;
		context.lineWidth = 1;
		context.strokeText(message, width / 2, DefaultSizing.Scale / 2);
	}

	context.fillStyle = textColor;
	context.fillText(message, width / 2, DefaultSizing.Scale / 2);
}
